import React, { ChangeEvent, Suspense, useCallback, useState } from 'react';

import { isNil } from 'lodash-es';
import { defineMessages, useIntl } from 'react-intl';
import styled, { css } from 'styled-components';

import { OnClick } from '@rbi-ctg/frontend';
import ActionButton from 'components/action-button';
import BoxInput from 'components/box-input';
import { IIconCheckCircleProps } from 'components/icons/check-circle';
import IconCreditCard from 'components/icons/minimal-credit-card';
import { LazyComponent, lazyWithNullFallback } from 'components/layout/util';
import Modal, { ModalContent, ModalHeading } from 'components/modal';
import Picture from 'components/picture';
import { usePrepaidTransactionCurrencyCodesQuery } from 'generated/rbi-graphql';
import { usePrepaidsBalance } from 'hooks/prepaid';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import { useFeatureLayout } from 'hooks/use-feature-layout';
import { useLocale } from 'state/intl';
import { usePaymentContext } from 'state/payment';
import { useUIContext } from 'state/ui';

const IconCheckCircle: LazyComponent<IIconCheckCircleProps> = lazyWithNullFallback(() =>
  import('components/icons/check-circle')
);

enum ModalStates {
  FORM = 'FORM',
  REVIEW = 'REVIEW',
  CONFIRM = 'CONFIRM'
}
interface ITransferBalanceModal {
  onDismiss: OnClick;
  modalAppearanceEventMessage?: string;
}

interface ISize {
  height?: string;
  width?: string;
}

interface ITextAlign {
  textAlign?: string;
  margin?: string;
}

const contentCss = css`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const ContentWrapper = styled.div`
  ${contentCss}
`;

const CenteredContentWrapper = styled.div`
  ${contentCss};
  justify-content: center;
`;

const CenteredContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TopSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #eeeaea;
  margin-bottom: 2.5rem;
  padding-bottom: 2rem;
  width: 100%;
  ${Styles.mobile`
    flex-direction: column;
  `};
`;

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: 400px;
`;

const FormInputs = styled.div`
  display: flex;
  flex-shrink: 0;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 1rem;
`;

const CardNumberContainer = styled.div`
  width: 250px;
  margin-bottom: 15px;
`;

const PinContainer = styled.div`
  width: 100px;
  margin-bottom: 15px;
`;

const ButtonContainer = styled.div<ISize>`
  width: ${p => p.width};
`;

const PlacementHolder = styled.div<ISize>`
  height: ${p => p.height};
  width: ${p => p.width};
`;

const TimCardContainer = styled.div`
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  width: 250px;
  height: 250px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  align-items: center;
  margin: 10px 20px;
`;

const HeadingText = styled.h4<ITextAlign>`
  color: #432726;
  font-size: 2rem;
  font-weight: bold;
  letter-spacing: -1.13px;
  line-height: 1.14;
  margin: ${p => p.margin || 0};
`;

const NormalText = styled.p<ITextAlign>`
  color: #432726;
  font-size: 1rem;
  line-height: 1.56;
  font-family: ${Styles.fontFamily.base};
  text-align: ${p => p.textAlign || 'center'};
  margin: ${p => p.margin || 0};
`;

const ErrorAnchor = styled.a`
  color: ${Styles.color.primary};
`;

const ErrorText = styled(NormalText)`
  color: ${Styles.color.primary};
  font-weight: ${Styles.fontWeight.heavy};
  margin-bottom: 1rem;
`;

const MobileSpacer = styled.div`
  ${Styles.mobile`
    height: 50vh;
  `}
`;

const INITIAL_SOURCE_CARD_DETAILS = {
  cardNumber: '',
  pin: '',
  currentBalance: 0
};

/**
 * @todo
 * re-implement form using Formik and superstruct
 * add validation
 * break up into smaller components
 */
const TransferBalanceModal = ({
  onDismiss,
  modalAppearanceEventMessage = 'Transfer prepaid card balance'
}: ITransferBalanceModal) => {
  const { formatMessage } = useIntl();
  const {
    getPrepaidPaymentMethod,
    mergePrepaidCardBalances,
    loading: paymentContextLoading
  } = usePaymentContext();
  const { formatCurrencyForLocale } = useUIContext();
  const { region } = useLocale();
  const { featureLayout } = useFeatureLayout();
  const [section, setSection] = useState(ModalStates.FORM);
  const [showBalanceLookupError, setShowBalanceLookupError] = useState(false);
  const [showTransferBalanceError, setShowTransferBalanceError] = useState(false);
  const [sourceCardDetails, setSourceCardDetails] = useState<{
    cardNumber: string;
    pin: string;
    currentBalance: number;
  }>(INITIAL_SOURCE_CARD_DETAILS);
  const {
    currentBalance,
    error: getBalanceError,
    getPrepaidsBalance,
    loading: getBalanceLoading
  } = usePrepaidsBalance({ region });
  const loading = getBalanceLoading || paymentContextLoading;

  const prepaidPaymentMethod = getPrepaidPaymentMethod();
  const prepaidPaymentMethodBalance = formatCurrencyForLocale(
    (prepaidPaymentMethod && prepaidPaymentMethod.prepaid!.currentBalance) || 0
  );

  // currentBalance is fine here as the prepaidBalance query passes countryCode
  const formattedBalanceToTransfer = formatCurrencyForLocale(sourceCardDetails.currentBalance);

  const handleCardNumberChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      setSourceCardDetails(currentCardDetails => ({ ...currentCardDetails, cardNumber: value }));
    },
    []
  );

  const handlePinChange = useCallback(({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setSourceCardDetails(currentCardDetails => ({ ...currentCardDetails, pin: value }));
  }, []);

  const handleGetCardBalance = useCallback(
    e => {
      e.preventDefault();

      // replace all `-` and spaces with an empty string
      const cardNumber = sourceCardDetails.cardNumber.replace(/[-\s]/g, '');

      return getPrepaidsBalance({ cardNumber });
    },
    [getPrepaidsBalance, sourceCardDetails.cardNumber]
  );

  const sourceCardNumber = sourceCardDetails.cardNumber;
  const prepaidCardNumber =
    (prepaidPaymentMethod &&
      prepaidPaymentMethod.prepaid &&
      prepaidPaymentMethod.prepaid.cardNumber) ||
    '';
  const {
    data: sourceCardTransactionCurrencyCodesData,
    loading: sourceCardTransactionCurrencyCodesLoading,
    error: sourceCardTransactionCurrencyCodesError
  } = usePrepaidTransactionCurrencyCodesQuery({
    variables: { cardNumber: sourceCardNumber }
  });
  const {
    data: prepaidCardTransactionCurrencyCodesData,
    loading: prepaidCardTransactionCurrencyCodesLoading
  } = usePrepaidTransactionCurrencyCodesQuery({
    variables: { cardNumber: prepaidCardNumber }
  });
  const currencyCodesLoading =
    prepaidCardTransactionCurrencyCodesLoading || sourceCardTransactionCurrencyCodesLoading;
  const currencyCodesData =
    !!prepaidCardTransactionCurrencyCodesData && !!sourceCardTransactionCurrencyCodesData;
  const sourceCardCurrencyCode =
    sourceCardTransactionCurrencyCodesData?.prepaidsTransactions?.transactions?.[0]?.currencyCode
      ?.number;
  const prepaidCardCurrencyCode =
    prepaidCardTransactionCurrencyCodesData?.prepaidsTransactions?.transactions?.[0]?.currencyCode
      ?.number;
  const cardCurrenciesMatch = prepaidCardCurrencyCode === sourceCardCurrencyCode;
  const cardCurrencyError =
    currencyCodesData &&
    !currencyCodesLoading &&
    !sourceCardTransactionCurrencyCodesError &&
    !cardCurrenciesMatch;

  const handleSubmit = useCallback(() => {
    mergePrepaidCardBalances({
      destinationFdAccountId: prepaidPaymentMethod && prepaidPaymentMethod.fdAccountId,
      sourceCardNumber: sourceCardDetails.cardNumber,
      sourcePin: sourceCardDetails.pin
    })
      .then(() => {
        setSection(ModalStates.CONFIRM);
      })
      .catch(() => {
        setShowTransferBalanceError(true);
      });
  }, [
    mergePrepaidCardBalances,
    prepaidPaymentMethod,
    sourceCardDetails.cardNumber,
    sourceCardDetails.pin
  ]);

  const handleConfirm = useCallback(
    e => {
      setSourceCardDetails(INITIAL_SOURCE_CARD_DETAILS);
      onDismiss(e);
    },
    [onDismiss]
  );

  useEffectOnUpdates(() => {
    setShowBalanceLookupError(!loading && !!getBalanceError);
    if (!isNil(currentBalance)) {
      setSourceCardDetails(currentDetails => ({ ...currentDetails, currentBalance }));
      setSection(ModalStates.REVIEW);
    }
  }, [getBalanceError, currentBalance]);

  const errorMessage = formatMessage(messages.transferBalanceError, {
    link: (
      <ErrorAnchor href="https://timchat.timhortons.com/feedback/">
        {formatMessage(messages.chatWithUs)}
      </ErrorAnchor>
    )
  });

  const giftCardImage = featureLayout?.giftCardImage?.locale;

  return (
    <Modal
      onDismiss={onDismiss}
      mParticleEventData={{
        modalAppearanceEventMessage
      }}
    >
      <ModalContent>
        <ModalHeading>{formatMessage(messages.transferBalance)}</ModalHeading>
        {section === ModalStates.FORM && (
          <ContentWrapper>
            <Suspense fallback={<PlacementHolder height="250px" width="250px" />}>
              <TimCardContainer>
                {giftCardImage && (
                  <Picture
                    image={giftCardImage}
                    alt={formatMessage(messages.giftCardImageAltText)}
                    objectFitContain
                  />
                )}
              </TimCardContainer>
            </Suspense>
            <NormalText margin="1rem 0 0 0">
              {formatMessage(messages.transferBalanceDirections)}
            </NormalText>

            <Form onSubmit={handleGetCardBalance}>
              <FormInputs>
                <CardNumberContainer>
                  <BoxInput
                    placeholder="XXXX-XXXX-XXXX-XXXX"
                    value={sourceCardDetails.cardNumber}
                    onChange={handleCardNumberChange}
                    renderIcon={() => <IconCreditCard height={20} />}
                    required
                  />
                </CardNumberContainer>
                <PinContainer>
                  <BoxInput
                    placeholder="PIN"
                    value={sourceCardDetails.pin}
                    onChange={handlePinChange}
                    required
                  />
                </PinContainer>
              </FormInputs>
              {showBalanceLookupError && <ErrorText>{errorMessage}</ErrorText>}
              {showTransferBalanceError && cardCurrencyError && (
                <ErrorText>{formatMessage(messages.cardCurrenciesDontMatch)}</ErrorText>
              )}
              <ButtonContainer width="175px">
                <ActionButton
                  type="submit"
                  isLoading={loading || currencyCodesLoading}
                  disabled={loading || currencyCodesLoading}
                >
                  {formatMessage(messages.continue)}
                </ActionButton>
              </ButtonContainer>
              <MobileSpacer />
            </Form>
          </ContentWrapper>
        )}

        {section === ModalStates.REVIEW && (
          <ContentWrapper>
            <TopSection>
              <Suspense fallback={<PlacementHolder height="91.35px" width="120.05px" />}>
                <TimCardContainer>
                  {giftCardImage && <Picture image={giftCardImage} alt="" />}
                </TimCardContainer>
              </Suspense>
              <TextContainer>
                <HeadingText>{formattedBalanceToTransfer}</HeadingText>
                <NormalText textAlign="left">
                  {formatMessage(messages.cardXHasABalanceOfY, {
                    cardNumber: sourceCardDetails.cardNumber,
                    balance: formattedBalanceToTransfer
                  })}
                </NormalText>
              </TextContainer>
            </TopSection>

            <BottomSection>
              <NormalText margin="1rem 0">
                {formatMessage(messages.yourBalanceXAddY, {
                  currentBalance: prepaidPaymentMethodBalance,
                  balanceToAdd: formattedBalanceToTransfer
                })}
              </NormalText>
              {showTransferBalanceError && <ErrorText>{errorMessage}</ErrorText>}
              <ButtonContainer width="255px">
                <ActionButton onClick={handleSubmit} isLoading={loading} disabled={loading}>
                  {formatMessage(messages.transferXToMyGiftCard, {
                    balance: formattedBalanceToTransfer
                  })}
                </ActionButton>
              </ButtonContainer>
            </BottomSection>
          </ContentWrapper>
        )}

        {section === ModalStates.CONFIRM && (
          <CenteredContentWrapper>
            <CenteredContent>
              <Suspense fallback={<PlacementHolder height="57px" width="57px" />}>
                <IconCheckCircle width="57" height="57" />
              </Suspense>
              <HeadingText margin="1.5rem 0 0">{formatMessage(messages.success)}</HeadingText>
              <NormalText margin="0 0 3.5rem">
                {formatMessage(messages.yourNewBalanceX, {
                  newBalance: prepaidPaymentMethodBalance
                })}
              </NormalText>
              <ButtonContainer width="180px">
                <ActionButton onClick={handleConfirm}>
                  {formatMessage(messages.closeWindow)}
                </ActionButton>
              </ButtonContainer>
            </CenteredContent>
          </CenteredContentWrapper>
        )}
      </ModalContent>
    </Modal>
  );
};

export default TransferBalanceModal;
const messages = defineMessages({
  closeWindow: {
    id: 'closeWindow',
    defaultMessage: 'Close Window',
    description: 'No description'
  },

  yourNewBalanceX: {
    id: 'yourNewBalanceX',
    defaultMessage: 'Your new Tim Card balance is {newBalance}',
    description: 'No description'
  },

  success: {
    id: 'success',
    defaultMessage: 'Success',
    description: 'No description'
  },

  transferXToMyGiftCard: {
    id: 'transferXToMyGiftCard',
    defaultMessage: 'Transfer {balance} to my Tim Card',
    description: 'No description'
  },

  yourBalanceXAddY: {
    id: 'yourBalanceXAddY',
    defaultMessage:
      'Your current balance is {currentBalance}. Would you like to add {balanceToAdd} to your Tim Card?',
    description: 'No description'
  },

  cardXHasABalanceOfY: {
    id: 'cardXHasABalanceOfY',
    defaultMessage: 'Card {cardNumber} has a balance of {balance}',
    description: 'No description'
  },

  continue: {
    id: 'continue',
    defaultMessage: 'Continue',
    description: 'No description'
  },

  cardCurrenciesDontMatch: {
    id: 'cardCurrenciesDontMatch',
    defaultMessage:
      'The Tim Card you are trying to transfer is from a different region than your account. Only Tim Cards from your region can be transferred.',
    description: 'No description'
  },

  transferBalanceDirections: {
    id: 'transferBalanceDirections',
    defaultMessage: 'First, enter the digits from the back of the card to look up the balance.',
    description: 'No description'
  },

  giftCardImageAltText: {
    id: 'giftCardImageAltText',
    defaultMessage: 'TimCard',
    description: 'No description'
  },

  transferBalance: {
    id: 'transferBalance',
    defaultMessage: 'Transfer Balance',
    description: 'No description'
  },

  chatWithUs: {
    id: 'chatWithUs',
    defaultMessage: 'chat with us',
    description: 'No description'
  },

  transferBalanceError: {
    id: 'transferBalanceError',
    defaultMessage:
      'We were unable to process your request. Please try again or {link} for assistance.',
    description: 'No description'
  }
});
